import * as d3 from "d3";
import { parseParameters } from "views/Config/Optimization/api";
import { getMaxTotal } from "views/Statistics/Throughput/Protocols/api";
import {
  atBqnTarget,
  parseSystemDate,
  parseSystemId,
  parseVersion,
} from "common/api";

export const LOAD_INTERVAL = 300000;
export const LOAD_DYNAMICS_INTERVAL = 2000;
export const LOAD_STATUS_INTERVAL = 15000;
export const DYNAMICS_INTERVAL_RETRY = 5000;
export const DPI_STATS_DASHBOARD_TMOUT_MS = 20000;
// The periods the dashboard offers, as the hours each of them covers. The
// name says whose they are: common/constants exports a PERIOD_TO_HOURS of
// its own, keyed by the periods of the reports, which are other ones.
export const PERIOD_DASHBOARD_TO_HOURS = {
  "1d": 24,
  "2d": 48,
  "3d": 72,
  "7d": 168
};

// The BQN aggregates the volume in intervals of minutes, so each period is
// asked for about the same number of points, near the ones the chart of the
// dashboard draws: the samples of five minutes of a week are 2016 rows to
// retrieve, to parse and to draw in a few hundred pixels.
export const PERIOD_TO_VOLUME_INTERVAL = {
  "1d": 5,
  "2d": 10,
  "3d": 15,
  "7d": 35,
};

// The chart of the application metrics draws a box for each interval the BQN
// reports, and only a few of them are legible in the width available, so each
// period is asked with the interval of minutes that covers it with this
// number of boxes.
export const APP_METRICS_TIME_POINTS = 15;

// The interval of minutes to ask for a period of hours, so that the BQN
// aggregates its samples into APP_METRICS_TIME_POINTS rows.
export const appMetricsTimeInterval = (hours) =>
  Math.max(1, Math.round((hours * 60) / APP_METRICS_TIME_POINTS));

// The limit of the license is drawn as a line of the chart only when the
// traffic comes near it: a BQN with no limit reported, or one licensed far
// above what it is passing, would flatten every area under a line at the top
// of it. The margin is the fraction of the traffic the limit may exceed.
const LIMIT_MARGIN = 1.2;

export const licenseThresholds = (limit, items) =>
  limit === undefined ||
  limit === null ||
  items.length === 0 ||
  limit > getMaxTotal(items) * LIMIT_MARGIN
    ? []
    : [{ value: limit, label: "License Limit" }];

// The items the legend of the card holds, the one that groups the rest of
// the CPUs included: a BQN of many of them would fill the card with a legend
// of its own, so only the first ones are named, and what is left goes under
// the last item, which draws all of it in one colour.
export const LEGEND_ITEMS_MAX = 10;
export const REST_LEGEND = "...rest";

// The fields of the chart, each with the colour of the item of the legend it
// belongs to: the ones grouped share the colour of the item that names them,
// so that what is drawn of them is what the legend shows.
export const getChartFields = (fields, colorsSet) => {
  const isGrouped = (index) =>
    fields.length > LEGEND_ITEMS_MAX && index >= LEGEND_ITEMS_MAX - 1;

  return fields.map((field, index) => {
    const colorIndex = isGrouped(index) ? LEGEND_ITEMS_MAX - 1 : index;

    return {
      name: field,
      label: field,
      color: colorsSet[colorIndex % colorsSet.length],
      legend: isGrouped(index) ? REST_LEGEND : field,
    };
  });
};

// One item of the legend per field, and one for all the fields grouped: the
// first of them gives the item its colour.
export const getLegendFields = (chartFields) =>
  chartFields.reduce((items, { color, legend }) => {
    return items.some(({ name }) => name === legend)
      ? items
      : [...items, { name: legend, label: legend, color }];
  }, []);

// The traffic each participant of a chart passes over the whole timeline,
// which tells the ones drawn on their own from the ones grouped.
const totalOfEach = (participants, items) =>
  items.reduce(
    (totals, item) =>
      participants.reduce(
        (totals, name) => ({
          ...totals,
          [name]: (totals[name] || 0) + (item[name] || 0),
        }),
        totals
      ),
    {}
  );

// The participants of a stacked chart with no more of them than the legend
// holds: the ones that pass the most traffic keep an item of their own, and
// what is left adds up into the last one. The areas of the chart still stack
// up to the whole of the traffic, which leaving the rest of them out would
// take away from it, and the ones named keep the order they come in, so that
// neither the colours nor the stack change with the traffic.
export const groupIntoRest = (participants, items, maxLegends = LEGEND_ITEMS_MAX) => {
  if (participants.length <= maxLegends) {
    return { participants, items };
  }

  const totals = totalOfEach(participants, items);
  const named = new Set(
    [...participants]
      .sort((one, other) => totals[other] - totals[one])
      .slice(0, maxLegends - 1)
  );
  const grouped = participants.filter((name) => !named.has(name));

  return {
    participants: [
      ...participants.filter((name) => named.has(name)),
      REST_LEGEND,
    ],
    items: items.map(({ time, ...values }) => ({
      time,
      ...Object.fromEntries(
        Object.entries(values).filter(([name]) => named.has(name))
      ),
      [REST_LEGEND]: grouped.reduce(
        (total, name) => total + (values[name] || 0),
        0
      ),
    })),
  };
};

export const PERIOD_DASHBOARD_PERIOD_MAP = {
  "1d": "Day",
  "2d": "TwoDays",
  "3d": "ThreeDays",
  "7d": "Week",
};

export const PERIOD_DASHBOARD_TO_TIME_PERIOD = {
  "1d": "oneDay",
  "2d": "twoDays",
  "3d": "threeDays",
  "7d": "oneWeek",
};

export const RETURN_VIEW = "viewDashboard";

// The command answers the hostname of a single BQN.
export const parseHostname = (response) => response.trim() || "Unknown";

// The system ID shown for that BQN, which may report none.
export const parseHardwareId = (response) =>
  parseSystemId(response) ?? "Unknown";

export const parseTimeStart = (response) =>
  response.match(/Time start: (.*)/)?.[1].substring(0, 25) || "unknown";

// The usage limit and the expiration of the "bta" feature.
export const parseLicenseBta = (response) => {
  const [header, ...rows] = response.trim().split("\n");
  const names = header.trim().split(/\s+/);
  const valueOf = (attrs, name) => attrs[names.indexOf(name)];
  const bta = rows
    .map((row) => row.trim().split(/\s+/))
    .find((attrs) => (valueOf(attrs, "FEATURE") || "").includes("bta"));

  return bta === undefined
    ? ["n/a", "n/a"]
    : [
        valueOf(bta, "USAGE-MAX") || "n/a",
        (valueOf(bta, "EXPIRATION") || "n/a").replace("T", " "),
      ];
};

// The subscriber groups active, or undefined when there is no count.
export const parseGroupsActive = (response) => {
  const [header, ...rows] = response.trim(/\s/).split("\n");
  const indexActive = header.trim(/\s/).split(/\s+/).indexOf("ACTIVE");
  const active =
    indexActive === -1 || rows.length === 0
      ? undefined
      : Number(rows[0].trim(/\s/).split(/\s+/)[indexActive]);

  return active === undefined || isNaN(active) ? undefined : active;
};

export const parseSubscribersActive = (response) =>
  (response || "")
    .trim()
    .split("\n")
    .reduce((acc, row) => {
      const [key, value] = row.trim().split(/\s+/);
      return key.startsWith("Active") && value !== undefined ? value : acc;
    }, 0);

// The readings of a response that reports one row of them under a header of
// names, each taken by the name of its own and normalized into what a card
// of the dashboard shows.
const readingsOf = (response) => {
  const [header, valuesRow] = response.trim().split("\n");
  const headerAttrs = header.trim().split(/\s+/);
  const values = (valuesRow || "").trim().split(/\s+/).filter(Boolean);

  return (attr, unit, precision) =>
    normalizeUnitsMayBeUndefined(
      values[headerAttrs.indexOf(attr)],
      unit,
      precision
    );
};

export const parseLatency = (response) => {
  const readingOf = readingsOf(response);

  // The latency of the card is the median of the samples of the downlink
  // taken in the last interval, which is the one the BQN reports of what it
  // is passing now, and not the minimum of the whole history of it. Only the
  // full command reports it.
  return {
    up: readingOf("RTT-MIN-UP-ms", "ms", 1),
    down: readingOf("RTT-INST-MED-DN-ms", "ms", 1),
  };
};

// The retransmissions of the access side, which are the ones of the traffic
// the BQN sends to the subscribers: the card shows no percentage of the
// internet side. The percentage is the one of the last seconds, which is what
// the BQN is passing now, and not the cumulative one since the packet engine
// started. Only the full command reports it.
export const parseRetransmissions = (response) =>
  readingsOf(response)("LAST-RTX%-ACCESS", "%", 2);

export const parseStatistics = (response) =>
  response
    .trim()
    .split("\n")
    .reduce((acc, row) => {
      const [metric1, value1, metric2, value2] = row.trim().split(/\s+/);
      if (metric1) {
        acc[metric1.slice(0, -1)] = parseFloat(value1);
      }
      if (metric2) {
        acc[metric2.slice(0, -1)] = parseFloat(value2);
      }
      return acc;
    }, {});

export const timeFormat = d3.timeFormat("%Y-%m-%d %H:%M:%S");

// The status of each alarm of that BQN.
export const parseAlarms = (response) => {
  const [header, ...rows] = response.trim().split("\n");
  const names = header.trim().split(/\s+/);
  const indexType = names.indexOf("TYPE");
  const indexStatus = names.indexOf("STATUS");

  return rows.reduce((alarms, row) => {
    const attrs = row.trim().split(/\s+/);

    return attrs[indexType] === undefined
      ? alarms
      : { ...alarms, [attrs[indexType]]: attrs[indexStatus] };
  }, {});
};

export const parseDateAndZone = (response) => {
  const attrs = response.trim(/\s/).split(/\s+/);
  const zone = attrs[attrs.length - 1];

  return `${timeFormat(parseSystemDate(response))} ${zone}`;
};

export function normalizeUnits(valueStr, units, precision) {
  var num = Number(valueStr);
  if (units == "Mbps" && num > 1000) {
    units = "Gbps";
    num /= 1000;
  }
  return num.toFixed(precision) + " " + units;
}

// What is shown of a reading that the BQN does not report.
const UNKNOWN = "Unknown";

const normalizeUnitsMayBeUndefined = (value, ...args) =>
  value === undefined || value === null
    ? UNKNOWN
    : value === "n/a"
      ? "n/a"
      : normalizeUnits(value, ...args);

// The value and the unit of a reading apart, which the cards of the
// dashboard show in one element each: "10.2 ms" is drawn as the number and a
// unit of its own beside it. A reading that is not a measure, such as the one
// of a BQN that reports none, has no unit to show.
export const valueAndUnit = (reading) => {
  if (reading === undefined || reading === null) {
    return { value: UNKNOWN, unit: "" };
  }

  const [value, unit = ""] = String(reading).split(" ");

  return { value, unit };
};

// A retrieval that repeats while the page is shown, and asks for nothing
// while it is hidden: the data would be retrieved, parsed and drawn for
// nobody, and the commands of the GUI are run one after the other, so it
// would also delay those of the page in front. The browser reporting the page
// shown again asks for the data right away, so that what is seen is never
// older than the interval.
//
// Only one request is ever on its way: the one that answers asks for the
// next, and the page being shown while one is out asks for nothing. The
// handler of an error tells whether to keep asking, and a failed retrieval
// waits for intervalRetry, which is the interval itself by default.
//
// RETURN VALUE:
//   The function that stops the retrievals.
export const pollWhileShown = ({
  retrieve,
  take,
  handleError,
  interval,
  intervalRetry = interval,
}) => {
  let isStopped = false;
  let isRetrieving = false;
  let timeoutId;

  const scheduleRetrieval = (delay) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(retrieveOnce, delay);
  };

  const retrieveOnce = () => {
    if (isStopped || isRetrieving) {
      return;
    }
    if (document.visibilityState !== "visible") {
      scheduleRetrieval(interval);
      return;
    }

    isRetrieving = true;
    retrieve()
      .then((data) => {
        isRetrieving = false;
        if (!isStopped) {
          take(data);
          scheduleRetrieval(interval);
        }
      })
      .catch((error) => {
        isRetrieving = false;
        if (isStopped) {
          return;
        }
        // A handler that says not to ask again stops the retrievals for
        // good: the page being shown again must not restart them.
        if (handleError(error)) {
          scheduleRetrieval(intervalRetry);
        } else {
          stop();
        }
      });
  };

  const stop = () => {
    isStopped = true;
    clearTimeout(timeoutId);
    document.removeEventListener("visibilitychange", retrieveOnce);
  };

  retrieveOnce();
  document.addEventListener("visibilitychange", retrieveOnce);

  return stop;
};

// A retrieval names the output of a command and the parser that takes it.
// The CLI runs the commands of the GUI one after the other, so the ones of a
// group go in a single batch request, which takes one turn of its queue
// instead of one per command. The response carries their outputs in order.
//
// The live data is the statistics of the packet engine, of which what is
// shown is the difference between two answers, the latency and the
// retransmissions, which the card of the access metrics shows as they are
// reported, and the date of the BQN: all of them are drawn at the cadence of
// the throughput and the packet rate.
const dynamicsRetrievals = [
  {
    name: "statisticsPktEng",
    command: "show pkteng statistics extended",
    parse: parseStatistics,
  },
  {
    name: "latencies",
    command: "show pkteng latency full",
    parse: parseLatency,
  },
  {
    name: "retransmissions",
    command: "show pkteng retransmission full",
    parse: parseRetransmissions,
  },
  { name: "systemTime", command: "show system date", parse: parseDateAndZone },
];

// The status of the BQN does not change from one moment to the next: the
// alarms and the counts of what it serves. Asking for it on a cadence of its
// own keeps it from taking a turn away from the live data above.
const statusRetrievals = [
  { name: "alarms", command: "show system alarms", parse: parseAlarms },
  {
    name: "activeSubs",
    command: "show subscriber usage",
    parse: parseSubscribersActive,
  },
  {
    name: "activeGroups",
    command: "show api subscriber-group status",
    parse: parseGroupsActive,
  },
  // The parameters of the packet engine tell which functionalities of the BQN
  // are active. They only change when it is configured, so they would be
  // asked for once, but they come with the status of the BQN to save a
  // request of their own: the profile of the user asks for the same command
  // at the node of the session, which is another BQN in a cluster.
  {
    name: "parameters",
    command: "show pkteng parameters",
    parse: parseParameters,
  },
];

// Every command of a batch carries the option of the BQN selected, except
// the ones marked as answering for the whole cluster, which take none. The
// CLI requires the request to end with an end of line.
export const composeBatch = (retrievals, mode, bqnId) =>
  `${retrievals
    .map(({ command, atCluster }) =>
      atCluster === true ? command : atBqnTarget(command, mode, bqnId)
    )
    .join("\n")}\n`;

// An output that reports an error is not parsed: the whole retrieval fails
// with it, as it did when each command was a request of its own, so that the
// caller still sees the error of a BQN that has gone offline.
const outputOfCommand = (retrievals, parts, index) => {
  const output = parts[index];
  const { command, mayFail } = retrievals[index];

  if (output === undefined) {
    throw `%ERR-EPROTO: No output of "${command}"`;
  }
  if (output.trim().startsWith("%ERR")) {
    // What a command of the group may answer without an output of its own is
    // taken as no data, and not as a failure of the whole request.
    if (mayFail === true) {
      return undefined;
    }

    throw output.trim();
  }

  return output;
};

// The separator is the one the CLI client inserts between the outputs of a
// batch request, which index.js publishes as a global.
export const parseBatch = (
  retrievals,
  response,
  separator = batchExModeSeparator
) => {
  const parts = response.split(separator);

  return retrievals.reduce((data, { name, parse }, index) => {
    const output = outputOfCommand(retrievals, parts, index);

    return { ...data, [name]: output === undefined ? undefined : parse(output) };
  }, {});
};

// The CLI client takes the settings of a request as an object, and the mode
// of a batch on its own when it needs none of them: only the batches whose
// commands take longer than the default, such as the ones of the DPI, give a
// timeout of their own.
const batchSettings = (settings) =>
  settings === undefined
    ? "batch_exmode"
    : { ...settings, isBatch: "batch_exmode" };

const retrieveBatchWith = (run, retrievals, mode, bqnId, settings) =>
  run(composeBatch(retrievals, mode, bqnId), batchSettings(settings)).then(
    (response) => parseBatch(retrievals, response),
  );

// The commands of the GUI run one after the other, so the batch takes its
// turn after the ones asked for before it.
export const retrieveBatch = (retrievals, mode, bqnId, settings) =>
  retrieveBatchWith(
    (cmd, cmdSettings) => ifCl.run(cmd, cmdSettings),
    retrievals,
    mode,
    bqnId,
    settings,
  );

// The same batch, run now instead of after them: for what is polled every
// few seconds and must not wait for the cards of a page to load.
export const retrieveBatchNow = (retrievals, mode, bqnId, settings) =>
  retrieveBatchWith(
    (cmd, cmdSettings) => ifCl.runNow(cmd, cmdSettings),
    retrievals,
    mode,
    bqnId,
    settings,
  );

export const composeDynamicsBatch = (mode, bqnId) =>
  composeBatch(dynamicsRetrievals, mode, bqnId);

export const parseDynamics = (response, separator) =>
  parseBatch(dynamicsRetrievals, response, separator);

// The throughput and the packet rate are the difference between two answers
// of the batch, so the dashboard shows them only once it has the second one.
// It is asked for beside the queue of the CLI: when the page loads, every
// card in view queues a retrieval as soon as the range is known, and a second
// answer that waited behind all of them would come only once they have all
// loaded, long after the counts of the status, which one answer gives.
export const getDynamics = (mode, bqnId) =>
  retrieveBatchNow(dynamicsRetrievals, mode, bqnId);

// What is shown of the BQN itself, which changes only when it is configured,
// licensed or restarted. The view asks for all of it at once, so a refresh of
// the dashboard takes one turn of the queue of the CLI instead of six.
const informationRetrievals = [
  { name: "version", command: "show version", parse: parseVersion },
  { name: "systemName", command: "show system hostname", parse: parseHostname },
  { name: "hardwareId", command: "show hardware id", parse: parseHardwareId },
  {
    name: "startTime",
    command: "show process name ctrmgr 0",
    parse: parseTimeStart,
  },
  // A BQN with no license answers no feature, and "n/a" is what is shown of
  // its expiration then, as it was when the command was a request of its own
  // and its error was only logged.
  {
    name: "license",
    command: "show license",
    parse: parseLicenseBta,
    mayFail: true,
  },
];

export const composeInformationBatch = (mode, bqnId) =>
  composeBatch(informationRetrievals, mode, bqnId);

export const parseInformation = (response, separator) =>
  parseBatch(informationRetrievals, response, separator);

export const getInformation = (mode, bqnId) =>
  retrieveBatch(informationRetrievals, mode, bqnId);

export const composeStatusBatch = (mode, bqnId) =>
  composeBatch(statusRetrievals, mode, bqnId);

export const parseStatus = (response, separator) =>
  parseBatch(statusRetrievals, response, separator);

export const getStatus = (mode, bqnId) =>
  retrieveBatch(statusRetrievals, mode, bqnId);

export function percentOver(value, total) {
  if (value == 0 || total == 0) {
    return "0 %";
  } else {
    const ratio = value / total;
    return ((ratio > 1.0 ? 1.0 : ratio) * 100).toFixed(1) + " %";
  }
}

export function getDbTCPOpt(
  tcpDnTx,
  prevTcpDnTx,
  tcpUpTx,
  prevTcpUpTx,
  tcpOptDn,
  prevTcpOptDn,
  tcpOptUp,
  prevTcpOptUp
) {
  const tcpTotal = tcpDnTx - prevTcpDnTx + (tcpUpTx - prevTcpUpTx);
  if (tcpTotal > 0) {
    const percentage = (
      ((tcpOptDn - prevTcpOptDn + (tcpOptUp - prevTcpOptUp)) * 100) /
      tcpTotal
    ).toFixed(1);
    return `${percentage} %`;
  }
  return "0 %";
}

export function getdbPps(value, prevValue, elapsed) {
  let dbPpsValue = "n/a";
  let dbPpsUnit = "Kpps";
  if (value >= prevValue) {
    dbPpsValue = value - prevValue;
    if (elapsed > 0) {
      dbPpsValue = dbPpsValue / elapsed;
    }
    if (dbPpsValue >= 1000000) {
      dbPpsValue = (dbPpsValue / 1000000).toFixed(3);
      dbPpsUnit = "Mpps";
    } else if (dbPpsValue > 0) {
      dbPpsValue = (dbPpsValue / 1000).toFixed(3);
    }
  }
  return { dbPpsValue, dbPpsUnit };
}

export function formatLargeStrNum(valueStr, precision) {
  return Number(valueStr).toLocaleString("en-US", {
    maximumFractionDigits: precision,
  });
}
